import { useI18n } from '../../i18n';
// ============================================
// Reset Password Page
// Form to set a new password using token from email
// ============================================

import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ADMIN_ROUTES } from '@conai/shared-constants';
import { env } from '../../config/env';
import { readJsonSafely, resolveApiErrorMessage } from '../../utils/http';
import { clearDeviceToken } from '../../utils/deviceToken';

export function ResetPasswordPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      return;
    }

    // In a real app, you'd validate the token with the backend
    // For now, we'll assume it's valid if it exists
    setTokenValid(true);
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (password !== confirmPassword) {
      setError(t('validationPasswordsDoNotMatch'));
      return;
    }

    // Validate password strength
    if (password.length < 8) {
      setError(t('validationPasswordMin8'));
      return;
    }

    setIsSubmitting(true);

    try {
      // Bug #7: đường dẫn cũ `/auth/reset-password` vừa SAI nhánh (BE dùng
      // /auth/customer/...) vừa THIẾU dấu "/" cuối. Django trả về trang lỗi HTML,
      // và `response.json()` trần bên dưới ném đúng câu "... is not valid JSON"
      // mà tester nhìn thấy — thay vì thông báo lỗi đọc được.
      const response = await fetch(`${env.apiBaseUrl}/auth/customer/reset-password/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, password }),
      });

      // Parse an toàn: body rỗng hoặc không phải JSON (404/502 HTML, proxy...)
      // thì coi như không có payload, KHÔNG để JSON.parse ném ra ngoài.
      const data = await readJsonSafely(response);

      if (!response.ok) {
        throw new Error(resolveApiErrorMessage(data, t('failedToResetPassword')));
      }

      // Đặt lại mật khẩu thu hồi TOÀN BỘ thiết bị tin cậy phía BE
      // (docs/ADMIN_2FA_EMAIL.md §7) — xoá device_token đã chết khỏi
      // localStorage, lần đăng nhập kế sẽ được hỏi mã lại là ĐÚNG thiết kế.
      clearDeviceToken();

      setSuccess(true);
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate(ADMIN_ROUTES.LOGIN);
      }, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('failedToResetPassword'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Invalid or missing token
  if (tokenValid === false) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 px-4">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-bold text-slate-800">{t('conaiStore')}</h1>
            <p className="mt-2 text-slate-600">{t('adminPortal')}</p>
          </div>

          <div className="rounded-2xl bg-white p-8 shadow-xl">
            <div className="text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
              </div>
              <h2 className="mt-4 text-xl font-semibold text-slate-900">{t('invalidResetLink')}</h2>
              <p className="mt-2 text-sm text-slate-600">
                This password reset link is invalid or has expired. Please request a new one.
              </p>
              <Link
                to={ADMIN_ROUTES.FORGOT_PASSWORD}
                className="mt-6 inline-flex w-full items-center justify-center rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Request New Link
              </Link>
              <Link
                to={ADMIN_ROUTES.LOGIN}
                className="mt-3 inline-flex w-full items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (tokenValid === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
        <div className="text-center">
          <svg className="mx-auto h-8 w-8 animate-spin text-blue-600" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <p className="mt-2 text-sm text-slate-600">{t('verifyingResetLink')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 px-4">
      <div className="w-full max-w-md">
        {/* Logo/Brand */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-slate-800">{t('conaiStore')}</h1>
          <p className="mt-2 text-slate-600">{t('adminPortal')}</p>
        </div>

        {/* Card */}
        <div className="rounded-2xl bg-white p-8 shadow-xl">
          <h2 className="text-2xl font-bold text-slate-900">{t('resetPassword')}</h2>
          <p className="mt-2 text-sm text-slate-600">
            Enter your new password below.
          </p>

          {/* Error Message */}
          {error && (
            <div className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Success Message */}
          {success ? (
            <div className="mt-6">
              <div className="rounded-lg bg-green-50 p-4">
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-green-800">{t('passwordResetSuccessful')}</h3>
                    <p className="mt-1 text-sm text-green-700">
                      Your password has been updated. You will be redirected to the login page shortly.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Form */
            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-slate-700">
                  New Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 block w-full rounded-lg border border-slate-300 px-4 py-2.5 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  placeholder="••••••••"
                  required
                  minLength={8}
                  autoFocus
                />
                <p className="mt-1 text-xs text-slate-500">
                  Must be at least 8 characters long
                </p>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700">
                  Confirm New Password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-1 block w-full rounded-lg border border-slate-300 px-4 py-2.5 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  placeholder="••••••••"
                  required
                  minLength={8}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Resetting...
                  </span>
                ) : (
                  'Reset Password'
                )}
              </button>
            </form>
          )}

          {/* Back to Login Link */}
          {!success && (
            <p className="mt-6 text-center text-sm text-slate-600">
              <Link to={ADMIN_ROUTES.LOGIN} className="font-medium text-blue-600 hover:text-blue-700">
                Back to Login
              </Link>
            </p>
          )}
        </div>

        {/* Footer */}
        <p className="mt-8 text-center text-xs text-slate-500">
          © 2024 ConAI. All rights reserved.
        </p>
      </div>
    </div>
  );
}
